/**
 * GlassCard — Premium card with Apple-style fluid glass background,
 * 3D lift on hover, and subtle border glow. Used for pathway cards,
 * stat cards, and content sections across the app.
 *
 * Usage:
 *   <GlassCard onClick={handleClick} accent="sage">
 *     <h3>Title</h3>
 *     <p>Description</p>
 *   </GlassCard>
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { fx } from "@/lib/effects";

type Accent = "sage" | "blue" | "amber" | "rose" | "neutral" | "teal" | "gold";

const ACCENT_STYLES: Record<Accent, string> = {
  sage: "hover:border-primary/30 hover:shadow-[0_8px_25px_-5px_rgba(71,130,95,0.12)]",
  teal: "hover:border-accent-teal/30 hover:shadow-[0_8px_25px_-5px_rgba(0,69,95,0.15)]",
  gold: "hover:border-accent-gold/30 hover:shadow-[0_8px_25px_-5px_rgba(212,160,23,0.15)]",
  blue: "hover:border-blue-400/30 hover:shadow-[0_8px_25px_-5px_rgba(59,130,246,0.12)]",
  amber: "hover:border-amber-400/30 hover:shadow-[0_8px_25px_-5px_rgba(217,119,6,0.12)]",
  rose: "hover:border-rose-400/30 hover:shadow-[0_8px_25px_-5px_rgba(225,29,72,0.12)]",
  neutral: "hover:border-border/60 hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.06)]",
};

interface GlassCardProps {
  children: React.ReactNode;
  accent?: Accent;
  onClick?: () => void;
  className?: string;
  as?: "div" | "button";
}

export function GlassCard({
  children,
  accent = "neutral",
  onClick,
  className,
  as = "div",
}: GlassCardProps) {
  const Comp = onClick ? "button" : as;

  return (
    <Comp
      onClick={onClick}
      className={cn(
        "rounded-xl border bg-card/80 backdrop-blur-sm p-5 text-left",
        "shadow-[0_1px_3px_-1px_rgba(0,0,0,0.06)]",
        fx.card,
        ACCENT_STYLES[accent],
        onClick && "cursor-pointer",
        "dark:bg-card/60 dark:border-border/50",
        className
      )}
    >
      {children}
    </Comp>
  );
}
